import React, { useMemo, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../utils/dateUtils';
import { DROPDOWN_FIELDS, TEXT_FIELDS } from '../hooks/useVehicleSaleReport';

const lowerFirst = (str) => (typeof str === 'string' && str.length > 0 ? str.charAt(0).toLowerCase() + str.slice(1) : str);

// Backend kontrati: data.filterOptions = { options: { "DealerName": [ { value, label, count } ], ... } }
// Saglamlik icin .options sarmali acilir; ayrica dict/array varyasyonlari da desteklenir.
// Hem PascalCase (backend kolon adi) hem camelCase (frontend field) anahtarlarini dener.
const getRawOptionsForColumn = (filterOptionsRaw, column, field) => {
    if (!filterOptionsRaw) return [];

    const filterOptions = filterOptionsRaw.options ?? filterOptionsRaw;
    if (!filterOptions) return [];

    if (Array.isArray(filterOptions)) {
        const entry = filterOptions.find((o) =>
            o && [o.column, o.columnName, o.name, o.key, o.field].some((k) => k === column || k === field)
        );
        if (!entry) return [];
        const list = entry.values ?? entry.options ?? entry.items ?? entry.data ?? entry.list ?? [];
        return Array.isArray(list) ? list : [];
    }

    if (typeof filterOptions === 'object') {
        const list =
            filterOptions[column] ??
            filterOptions[field] ??
            filterOptions[lowerFirst(column)] ??
            [];
        return Array.isArray(list) ? list : [];
    }

    return [];
};

const normalizeOptions = (rawList) => {
    if (!Array.isArray(rawList)) return [];
    return rawList
        .filter((opt) => opt !== null && opt !== undefined && opt !== '')
        .map((opt) => {
            if (typeof opt === 'object') {
                const value = opt.value ?? opt.id ?? opt.key ?? opt.text ?? opt.name ?? opt.label;
                const rawLabel = opt.label ?? opt.text ?? opt.name ?? opt.displayName ?? value;
                return { value, label: rawLabel };
            }
            return { value: opt, label: opt };
        });
};

export default function VehicleSaleReportTable({
    columns,
    items,
    totalCount,
    loading,
    lazyParams,
    onPage,
    sortField,
    sortOrder,
    onSortChange,
    activeFilters,
    onFiltersChange,
    filterOptions
}) {
    const { th } = useLanguage();
    const dataTableRef = useRef(null);

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const rows = Array.isArray(items)
        ? items.map((item, index) => ({ ...item, _rowId: `${lazyParams.first}-${index}` }))
        : [];

    const tableFilters = useMemo(() => {
        const filters = {};
        TEXT_FIELDS.forEach((field) => {
            filters[field] = {
                value: activeFilters?.[field] ?? null,
                matchMode: activeFilters?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS
            };
        });
        DROPDOWN_FIELDS.forEach((field) => {
            filters[field] = {
                value: activeFilters?.[field] ?? null,
                matchMode: FilterMatchMode.EQUALS
            };
        });
        return filters;
    }, [activeFilters]);

    const handleTableFilter = (e) => {
        const filters = e?.filters || {};
        const updated = {};

        TEXT_FIELDS.forEach((field) => {
            if (field in filters) {
                updated[field] = filters[field]?.value ?? null;
                updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
            }
        });
        DROPDOWN_FIELDS.forEach((field) => {
            if (field in filters) {
                updated[field] = filters[field]?.value ?? null;
            }
        });

        onFiltersChange?.(updated);
    };

    const dropdownOptionsByField = useMemo(() => {
        const result = {};
        if (!filterOptions) return result;
        columns
            .filter((col) => col.type === 'dropdown')
            .forEach((col) => {
                const rawList = getRawOptionsForColumn(filterOptions, col.column, col.field);
                result[col.field] = normalizeOptions(rawList);
            });
        return result;
    }, [filterOptions, columns]);

    const renderDropdownFilter = (field, options) => (
        <Dropdown
            value={options.value}
            options={dropdownOptionsByField[field] || []}
            onChange={(e) => options.filterApplyCallback(e.value)}
            placeholder={th('common.all')}
            showClear
            filter
            className="p-column-filter"
            style={{ minWidth: '12rem' }}
        />
    );

    const renderColumnBody = (col) => {
        if (col.type === 'date') {
            return (row) => (row?.[col.field] ? formatDateOnly(row[col.field]) : '-');
        }
        return (row) => (row?.[col.field] != null && row[col.field] !== '' ? row[col.field] : '-');
    };

    const buildColumn = (col) => {
        const common = {
            key: col.field,
            field: col.field,
            header: col.header,
            sortable: true,
            style: { minWidth: col.minWidth || '160px' },
            body: renderColumnBody(col),
            bodyClassName: 'text-center',
            headerClassName: 'text-center'
        };

        if (col.type === 'text') {
            return (
                <Column
                    {...common}
                    filter
                    filterField={col.field}
                    filterPlaceholder={th('common.search')}
                    showFilterMenu={true}
                    filterMatchModeOptions={textMatchModeOptions}
                    showClearButton={false}
                />
            );
        }

        if (col.type === 'dropdown') {
            return (
                <Column
                    {...common}
                    filter
                    filterField={col.field}
                    showFilterMenu={false}
                    filterElement={(options) => renderDropdownFilter(col.field, options)}
                />
            );
        }

        return <Column {...common} />;
    };

    return (
        <DataTable
            value={rows}
            dataKey="_rowId"
            lazy
            paginator
            rows={lazyParams.rows}
            first={lazyParams.first}
            totalRecords={totalCount}
            onPage={onPage}
            loading={loading}
            emptyMessage={th('common.noRecords')}
            rowsPerPageOptions={[10, 25, 50]}
            showGridlines
            tableStyle={{ minWidth: '60rem' }}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSortChange}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            ref={dataTableRef}
        >
            {columns.map((col) => buildColumn(col))}
        </DataTable>
    );
}
